import { useCallback, useEffect, useRef } from 'react';

import { useLazyQuery } from '@apollo/client/react';

import AssignmentPassingThresholdQuery from 'bundles/assess-common/api/AssignmentPassingThreshold.graphql';
import type {
  AssignmentPassingThresholdQuery as AssignmentPassingThresholdQueryData,
  AssignmentPassingThresholdQueryVariables,
} from 'bundles/assess-common/api/__generated__/AssignmentPassingThreshold';
import { getWorkspaceV2Launcher } from 'bundles/labs-common/utils/WorkspaceApiUtils';
import { getSkillAssessmentMappingType } from 'bundles/skills-common/shared/verified-skills/utils/verifiedSkillsUtils';

export type SkillAssessmentIdAndPassingFraction = {
  assessmentId: string;
  passingFraction: number | null;
  assessmentType: string;
};

type Props = {
  itemId: string;
  itemTypeName?: string;
  courseId: string;
};

const useGetSkillAssessmentIdAndPassingFraction = () => {
  const [fetchPassingThreshold] = useLazyQuery<
    AssignmentPassingThresholdQueryData,
    AssignmentPassingThresholdQueryVariables
  >(AssignmentPassingThresholdQuery, {
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-first',
  });

  const fetchPassingThresholdRef = useRef(fetchPassingThreshold);

  useEffect(() => {
    fetchPassingThresholdRef.current = fetchPassingThreshold;
  });

  const fetchSkillAssessmentIdAndPassingFraction = useCallback(
    async ({ itemId, itemTypeName, courseId }: Props): Promise<SkillAssessmentIdAndPassingFraction> => {
      const assessmentType = getSkillAssessmentMappingType(itemTypeName);

      if (assessmentType === 'lab') {
        const launcher = await getWorkspaceV2Launcher(courseId, itemId);

        return {
          assessmentId: launcher?.versionedAtomId || '',
          passingFraction: null,
          assessmentType,
        };
      }

      const passingThresholdResult = await fetchPassingThresholdRef.current({
        variables: {
          itemId,
          courseId,
        },
      });

      if (
        passingThresholdResult.error ||
        passingThresholdResult.data?.SubmissionState?.queryState?.__typename !== 'Submission_SubmissionState'
      ) {
        throw new Error('Failed to fetch passing threshold');
      }

      const queryState = passingThresholdResult.data?.SubmissionState?.queryState;
      const assignmentState = queryState?.__typename === 'Submission_SubmissionState' ? queryState.assignment : null;

      return {
        assessmentId: assignmentState?.id?.split('@')[0] || '',
        passingFraction: assignmentState?.passingFraction || 1,
        assessmentType,
      };
    },
    []
  );

  return { fetchSkillAssessmentIdAndPassingFraction };
};

export default useGetSkillAssessmentIdAndPassingFraction;
